import {
  Button,
  CheckIcon,
  CloseIcon,
  EditIcon,
  GenerateIcon,
  InfoIcon,
  MessagesDefaultIcon,
  QuestionmarkIcon,
  UndoIcon,
  UpdateIcon,
  UploadIcon,
} from '@bp/ui-components';
import classNames from 'classnames';
import { TipTapTextExtender } from 'components/TipTap/TipTapTextExtender';
import dayjs from 'dayjs';
import { MatrixEvent } from 'matrix-js-sdk';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NotificationReason } from 'utils/matrixClient';
import styles from './ActivityProtocolEntry.module.scss';

type ActivityEvent = {
  type: NotificationReason;
  clicked?: boolean;
};

type ActivityProtocolEntryProps = {
  event: MatrixEvent;
  isTeacher: boolean;
  onGrantFeedbackRequest: (eventId: string) => void;
  onRejectFeedbackRequest: (eventId: string) => void;
  onPublishSubmission: (eventId: string, isPublic: boolean) => void;
  className?: string | undefined;
};

export const ActivityProtocolEntry = ({
  event,
  isTeacher,
  onGrantFeedbackRequest,
  onRejectFeedbackRequest,
  onPublishSubmission,
  className,
}: ActivityProtocolEntryProps) => {
  const { t } = useTranslation();

  const [buttonsVisible, setButtonsVisible] = useState<boolean>(true);

  const body = event.getContent().body;
  const eventId = event.getId();
  const fromNow = dayjs(event.getDate()).fromNow();

  let type: NotificationReason | null = null;
  let clicked: boolean | undefined = undefined;

  try {
    const response = JSON.parse(body) as ActivityEvent;
    if (response && response.type) type = response.type;
    if (response && response.clicked) clicked = response.clicked;
  } catch {
    if (body && typeof body === 'string') type = NotificationReason.SubmissionComment;
  }

  function getColor(): string {
    switch (type) {
      case NotificationReason.SubmissionNew:
      case NotificationReason.SubmissionPublished:
        return styles.green;
      case NotificationReason.SubmissionComment:
      case NotificationReason.SubmissionFeedbackRejected:
      case NotificationReason.SubmissionDePublished:
        return styles.blue;
      case NotificationReason.SubmissionAnswer:
      case NotificationReason.AssignmentOpened:
      case NotificationReason.SubmissionUpdated:
      case NotificationReason.SubmissionRequestToPublish:
        return styles.grey;
      case NotificationReason.SubmissionFeedback:
        return styles.orange;
      case NotificationReason.SubmissionRequestToFeedback:
      case NotificationReason.SubmissionResponse:
        return isTeacher ? styles.grey : styles.blue;
      case NotificationReason.AssignmentDeleted:
        return styles.red;
      default:
        return '';
    }
  }

  function getIcon(): ReactNode {
    switch (type) {
      case NotificationReason.SubmissionNew:
        return <CheckIcon style={{ marginLeft: '1px' }} />;
      case NotificationReason.SubmissionComment:
      case NotificationReason.SubmissionAnswer:
        return <MessagesDefaultIcon style={{ marginTop: '1px', marginLeft: '1px' }} />;
      case NotificationReason.AssignmentOpened:
        return <GenerateIcon />;
      case NotificationReason.SubmissionUpdated:
        return <EditIcon />;
      case NotificationReason.SubmissionFeedback:
        return <UpdateIcon />;
      case NotificationReason.SubmissionRequestToFeedback:
        return isTeacher ? <QuestionmarkIcon /> : <UpdateIcon />;
      case NotificationReason.SubmissionFeedbackRejected:
        return <InfoIcon />;
      case NotificationReason.AssignmentDeleted:
        return <CloseIcon />;
      case NotificationReason.SubmissionRequestToPublish:
        return <QuestionmarkIcon />;
      case NotificationReason.SubmissionPublished:
        return <UploadIcon style={{ marginLeft: '1px' }} />;
      case NotificationReason.SubmissionDePublished:
      case NotificationReason.SubmissionResponse:
        return <UndoIcon style={{ marginRight: '2px' }} />;
      default:
        return <></>;
    }
  }

  function getTranslation(): string {
    switch (type) {
      case NotificationReason.SubmissionNew:
        return t('activityProtocol.submissionNew');
      case NotificationReason.SubmissionComment:
        return t('activityProtocol.comment');
      case NotificationReason.SubmissionAnswer:
        return t('activityProtocol.answer');
      case NotificationReason.AssignmentOpened:
        return t('activityProtocol.assignmentOpened');
      case NotificationReason.SubmissionUpdated:
        return t('activityProtocol.submissionWasEdited');
      case NotificationReason.SubmissionFeedback:
        return t('activityProtocol.submissionFeedback');
      case NotificationReason.SubmissionRequestToFeedback:
        return isTeacher
          ? t('activityProtocol.requestToFeedbackTeacher')
          : t('activityProtocol.requestToFeedbackStudent');
      case NotificationReason.SubmissionFeedbackRejected:
        return t('activityProtocol.feedbackRejected');
      case NotificationReason.AssignmentDeleted:
        return t('activityProtocol.assignmentClosed');
      case NotificationReason.SubmissionRequestToPublish:
        return isTeacher
          ? t('activityProtocol.requestToPublishTeacher')
          : t('activityProtocol.requestToPublishStudent');
      case NotificationReason.SubmissionPublished:
        return t('activityProtocol.submissionPublished');
      case NotificationReason.SubmissionDePublished:
        return t('activityProtocol.submissionDePublished');
      case NotificationReason.SubmissionResponse:
        return isTeacher ? t('activityProtocol.responseTeacher') : t('activityProtocol.responseStudent');
      default:
        return '';
    }
  }

  function handleGrantFeedbackRequest() {
    if (eventId) {
      setButtonsVisible(false);
      onGrantFeedbackRequest(eventId);
    }
  }

  function handleRejectFeedbackRequest() {
    if (eventId) {
      setButtonsVisible(false);
      onRejectFeedbackRequest(eventId);
    }
  }

  function handlePublishRequest(isPublic: boolean) {
    if (eventId) {
      setButtonsVisible(false);
      onPublishSubmission(eventId, isPublic);
    }
  }

  const showMessage =
    (type === NotificationReason.SubmissionComment ||
      type === NotificationReason.SubmissionAnswer ||
      (type === NotificationReason.SubmissionRequestToFeedback && !isTeacher) ||
      (type === NotificationReason.SubmissionRequestToPublish && !isTeacher)) &&
    body &&
    body !== '';

  return (
    <div className={classNames(styles['activity-protocol-entry'], getColor(), className)}>
      <div className={styles['icon-wrap']}>
        <div className={styles.icon}>{getIcon()}</div>
      </div>
      <div className={styles.body}>
        <div className={styles.text}>{getTranslation()}</div>
        <div className={styles.time}>{fromNow}</div>

        {/* MESSAGE */}
        {showMessage && (
          <div className={styles.message}>
            <TipTapTextExtender
              uuid={event.getId() ?? String(event.getDate())}
              maxPixelHeight={46}
              padding='none'
              content={event.getContent().formatted_body ?? body}
            />
          </div>
        )}

        {/* ACTIONS */}
        {type === NotificationReason.SubmissionRequestToFeedback && isTeacher && buttonsVisible && (
          <div className={styles.buttons}>
            <Button onClick={() => handleGrantFeedbackRequest()} icon={<CheckIcon />}>
              {t('common.agree')}
            </Button>
            <Button onClick={() => handleRejectFeedbackRequest()} hierarchy={'secondary'} icon={<CloseIcon />}>
              {t('common.reject')}
            </Button>
          </div>
        )}
        {type === NotificationReason.SubmissionRequestToPublish && !isTeacher && !clicked && (
          <div className={styles.buttons}>
            <Button icon={<CheckIcon />} onClick={() => handlePublishRequest(true)}>
              {t('common.agree')}
            </Button>
            <Button hierarchy={'secondary'} icon={<CloseIcon />} onClick={() => handlePublishRequest(false)}>
              {t('common.reject')}
            </Button>
          </div>
        )}
        {type === NotificationReason.SubmissionPublished && !isTeacher && (
          <div className={styles.buttons}>
            <Button icon={<UndoIcon />} onClick={() => handlePublishRequest(false)}>
              {t('common.undo')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
